import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';

import {
  fetchNotifications,
  markRead,
  markAllRead,
  selectNotifications,
  selectUnreadCount,
  selectNotificationsNext,
  selectNotificationsLoading,
  selectNotificationsError,
} from '../store/notifications/notificationsSlice';

const NotificationPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);

  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const next = useSelector(selectNotificationsNext);
  const loading = useSelector(selectNotificationsLoading);
  const error = useSelector(selectNotificationsError);

  // AppHeader mounts once per session, so without this the badge would only ever
  // reflect the first page load. Refetch whenever the route changes.
  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch, location.pathname]);

  const open = Boolean(anchor);

  const handleOpenNotification = (notification) => {
    if (notification.read_at === null) {
      dispatch(markRead(notification.id));
    }
    setAnchor(null);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            background: alpha(theme.palette.text.primary, 0.08),
            borderRadius: '50%',
            flexShrink: 0,
            '&:hover': { background: alpha(theme.palette.text.primary, 0.15) },
          }}
          aria-label="Notifications"
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: { xs: 'calc(100vw - 32px)', sm: 380 }, maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
            <Button size="small" onClick={() => dispatch(markAllRead())} disabled={unreadCount === 0}>
              Mark all read
            </Button>
          </Box>

          {error && (
            <Typography variant="body2" color="error" sx={{ px: 2, pb: 1 }} role="alert">{error}</Typography>
          )}

          {notifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
              {loading ? 'Loading...' : 'You have no notifications.'}
            </Typography>
          ) : (
            <List disablePadding sx={{ overflowY: 'auto' }}>
              {notifications.map((n) => {
                const unread = n.read_at === null;
                return (
                  <ListItemButton
                    key={n.id}
                    onClick={() => handleOpenNotification(n)}
                    sx={{
                      alignItems: 'flex-start',
                      gap: 1.5,
                      background: unread ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        mt: 0.75,
                        width: 8,
                        height: 8,
                        flexShrink: 0,
                        borderRadius: '50%',
                        background: unread ? theme.palette.primary.main : 'transparent',
                      }}
                    />
                    <Box>
                      <Typography variant="body2" color={unread ? 'text.primary' : 'text.secondary'}>
                        {n.summary}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(n.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}

          {next && (
            <Button onClick={() => dispatch(fetchNotifications(next))} disabled={loading} sx={{ m: 1 }}>
              Load more
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationPanel;
